import { Link } from "react-router-dom";
import {
  Truck,
  ShieldCheck,
  Package,
  Sparkles,
  Calendar,
  Layers,
  Boxes,
  BookOpen,
  Scale,
  CircleCheck,
  AlertTriangle,
  Tag,
  ArrowRight,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison personnalisée sur le secteur ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Livraison personnalisée
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Votre livraison de cartons personnalisée sur le secteur
              {" "}{city.aCity}
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Notre service de transport prend en charge le dépôt
              immédiat de vos colis directement à votre porte ou au sein
              de votre propriété villepreusienne, s&apos;étendant
              également aux localités adjacentes des Clayes-sous-Bois,
              de Saint-Nom-la-Bretèche, de Chavenay, de
              Fontenay-le-Fleury et de Rennemoulin.
            </p>
            <p className="mt-4 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Nous établissons un horaire strict et respecté afin de
              préserver la fluidité de votre emploi du temps. Chaque lot
              est fourni à plat sous enveloppe hermétique isolante,
              garantissant des parois saines, exemptes de poussière et
              prêtes à l&apos;assemblage.
            </p>
          </div>

          <div className="lg:col-span-5 space-y-4">
            {[
              {
                title: "Dépôt direct à votre porte",
                body: "Villepreux, Les Clayes-sous-Bois, Saint-Nom-la-Bretèche, Chavenay, Fontenay-le-Fleury, Rennemoulin.",
                Icon: Truck,
              },
              {
                title: "Créneau horaire strict",
                body: "Un rendez-vous précis, respecté à la minute, pour préserver la fluidité de votre emploi du temps.",
                Icon: Calendar,
              },
              {
                title: "Cartons sous enveloppe isolante",
                body: "Livrés à plat, parois saines, exemptes de poussière, prêtes à être assemblées immédiatement.",
                Icon: ShieldCheck,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="flex items-start gap-4 bg-white border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
              >
                <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <div>
                  <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                    {title}
                  </h3>
                  <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                    {body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Double cannelure ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Bouclier double cannelure
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Une résistance adaptée aux charges importantes
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Mettre des objets lourds dans des contenants inadaptés est
            la cause principale de rupture des fonds lors du portage.
            Pour vos effets les plus denses, notre cartonnerie lourde
            prend le relais.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {[
            {
              title: "Épaisseur renforcée",
              body: "Conçus en carton ondulé double cannelure de calibre industriel, ces modèles ne plient pas sous l'effort et empêchent tout affaissement vertical dans le véhicule de transport.",
              Icon: Layers,
            },
            {
              title: "Idéal pour le lourd",
              body: "Ce matériel est spécifiquement calibré pour supporter vos piles de livres, votre vaisselle en faïence, vos dossiers administratifs compacts ou vos petits appareils électroménagers, permettant un empilement stable dans le camion.",
              Icon: BookOpen,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
            >
              <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Icon className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <div>
                <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-villepreux-cta-double-cannelure"
          >
            <Boxes className="h-4 w-4" strokeWidth={2.6} />
            Voir les cartons double cannelure
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Comment bien préparer ses cartons ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Méthode pro
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Comment bien préparer ses cartons&nbsp;?
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Pour maximiser l&apos;espace et garantir l&apos;équilibre de
            vos boîtes, appliquez cette méthode rigoureuse.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {[
            {
              title: "Lourds en bas, légers en haut",
              body: "Disposez systématiquement les volumes lourds au fond du contenant et les éléments légers sur la partie supérieure pour préserver la structure du carton.",
              Icon: Scale,
            },
            {
              title: "Comblez les zones de vide",
              body: "Comblez impérativement les espaces vides avec du calage ou du linge de maison pour empêcher l'écrasement de la boîte lors de l'empilement.",
              Icon: Layers,
            },
            {
              title: "Maximum 20 à 25 kg par carton",
              body: "Évitez de dépasser la charge maximale de 20 à 25 kg par unité pour préserver la sécurité des manutentionnaires et la rigidité du carton.",
              Icon: AlertTriangle,
            },
            {
              title: "Le scellage en « H »",
              body: "Appliquez un ruban adhésif de qualité en formant un « H » sur le dessous et le dessus de la boîte pour sceller parfaitement la structure.",
              Icon: ShieldCheck,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
            >
              <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Icon className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <div>
                <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-bold text-[#237cb5]">
                  <CircleCheck className="h-3.5 w-3.5" strokeWidth={2.8} />
                  Méthode pro
                </div>
                <h3 className="mt-1 font-display font-extrabold text-[16px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/fermeture-accessoires"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-villepreux-cta-accessoires"
          >
            <Tag className="h-4 w-4" strokeWidth={2.6} />
            Adhésifs &amp; accessoires pro
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/emballage-protection"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-villepreux-cta-protection"
          >
            <Sparkles className="h-4 w-4" strokeWidth={2.6} />
            Solutions de calage
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-t border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-villepreux-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
